import { computed, ref, watch } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

const DEFAULT_DEBOUNCE_MS = 300
const DEFAULT_MIN_SEARCH_LENGTH = 3

/** Props subset the search engine reads. Both searchable wrappers' props
 *  (`AppSelectSearchableProps` / `AppMultiSelectSearchableProps`) structurally
 *  satisfy this — pass `props` straight through. */
interface SearchEngineProps<TValue = string | number> {
  options: AppSelectOption<TValue>[]
  /** Min query length before `@search` emits. Default: `3`. Server-mode only. */
  minSearchLength?: number
  /** Debounce window for `@search` while typing. `0` disables. Default: `300`. */
  debounceMs?: number
  /** In-flight flag. Suppresses no-results and disables the clear button. */
  loading?: boolean
  /** Server pagination mode. When `false` — client-side filter only. */
  infinite?: boolean
  /** Whether more pages are available. Used only when `infinite=true`. */
  hasMore?: boolean
}

interface UseSelectSearchOptions<TValue = string | number> {
  props: SearchEngineProps<TValue>
  onSearch: (query: string) => void
  onLoadMore: (query: string) => void
}

interface UseSelectSearchReturn<TValue = string | number> {
  /** Live query string (writable — input v-model binds through the wrapper). */
  query: Ref<string>
  /** Whether the trigger input is focused (drives query-vs-text display). */
  isFocused: Ref<boolean>
  /** Options to render: client filter, or server list while paginating. */
  displayedOptions: ComputedRef<AppSelectOption<TValue>[]>
  showClearButton: ComputedRef<boolean>
  showNoResults: ComputedRef<boolean>
  isClearDisabled: ComputedRef<boolean>
  isLoadMoreDisabled: ComputedRef<boolean>
  handleInputBlur: () => void
  handleLoadMoreClick: () => void
  /** Reset query to '' (no-op when already empty). Call from the wrapper's
   *  `onClose` so the debounce watcher emits `@search('')` if it was non-empty. */
  resetQuery: () => void
}

/**
 * Shared search engine for the searchable selects. Owns query state, the
 * client/server `displayedOptions` resolution, the UI flags, the debounced
 * `@search` watcher, and the load-more / blur handlers.
 *
 * NOT owned here (they depend on the core's `inputRef`, which is built from
 * this engine's `displayedOptions`): `handleInputFocus` and `handleClearClick`
 * stay in each wrapper as thin 2-3 line composers over `isFocused`/`query`.
 */
export const useSelectSearch = <TValue = string | number>(
  options: UseSelectSearchOptions<TValue>,
): UseSelectSearchReturn<TValue> => {
  const { props, onSearch, onLoadMore } = options

  const query = ref<string>('')
  const isFocused = ref<boolean>(false)
  const lastEmittedQuery = ref<string>('')

  const minSearchLen = computed<number>(
    () => props.minSearchLength ?? DEFAULT_MIN_SEARCH_LENGTH,
  )

  const normalizedQuery = computed<string>(() => query.value.trim())

  const filterByText = (
    opts: AppSelectOption<TValue>[],
    q: string,
  ): AppSelectOption<TValue>[] => {
    const needle = q.trim().toLowerCase()
    if (needle === '') return opts
    return opts.filter(o => o.text.toLowerCase().includes(needle))
  }

  const displayedOptions = computed<AppSelectOption<TValue>[]>(() => {
    if (!props.infinite) return filterByText(props.options, query.value)
    if (!props.hasMore && lastEmittedQuery.value === '') {
      return filterByText(props.options, query.value)
    }
    return props.options
  })

  const showClearButton = computed<boolean>(() => query.value !== '')
  const isClearDisabled = computed<boolean>(() => Boolean(props.loading))

  const showNoResults = computed<boolean>(() => {
    if (props.loading) return false
    if (normalizedQuery.value.length === 0) return false
    if (props.infinite && props.hasMore && normalizedQuery.value.length < minSearchLen.value) return false
    return displayedOptions.value.length === 0
  })

  const isLoadMoreDisabled = computed<boolean>(() => {
    if (normalizedQuery.value.length === 0) return false
    return normalizedQuery.value.length < minSearchLen.value
  })

  const handleInputBlur = (): void => {
    isFocused.value = false
  }

  const handleLoadMoreClick = (): void => {
    if (!props.infinite || !props.hasMore) return
    if (props.loading) return
    if (isLoadMoreDisabled.value) return
    onLoadMore(normalizedQuery.value)
  }

  const resetQuery = (): void => {
    if (query.value !== '') {
      query.value = ''
    }
  }

  // ── @search debounce watcher ─────────────────────────
  let searchTimer: ReturnType<typeof setTimeout> | null = null

  const cancelDebounce = (): void => {
    if (searchTimer !== null) {
      clearTimeout(searchTimer)
      searchTimer = null
    }
  }

  const emitSearch = (q: string): void => {
    lastEmittedQuery.value = q
    onSearch(q)
  }

  watch(query, () => {
    const q = normalizedQuery.value
    if (!props.infinite) return
    if (!props.hasMore && lastEmittedQuery.value === '') return
    cancelDebounce()

    if (q.length >= minSearchLen.value) {
      const delay = props.debounceMs ?? DEFAULT_DEBOUNCE_MS
      if (delay <= 0) {
        emitSearch(q)
        return
      }
      searchTimer = setTimeout(() => {
        emitSearch(q)
        searchTimer = null
      }, delay)
      return
    }

    if (lastEmittedQuery.value !== '') {
      emitSearch('')
    }
  })

  return {
    query,
    isFocused,
    displayedOptions,
    showClearButton,
    showNoResults,
    isClearDisabled,
    isLoadMoreDisabled,
    handleInputBlur,
    handleLoadMoreClick,
    resetQuery,
  }
}
